import { queryOptions } from '@tanstack/react-query';
import { getApartmentCalendar, type CalendarDay as ApiCalendarDay } from '@/api/generated';
import { HttpError } from '@/lib/apartments';

// CalendarMap indexes the calendar by its YYYY-MM-DD date string. String keys,
// not Date objects: two Dates for the same calendar day are not equal, and the
// API speaks plain calendar dates with no time zone.
export type CalendarMap = ReadonlyMap<string, ApiCalendarDay>;

// addDays shifts a YYYY-MM-DD string by n days. Uses UTC arithmetic on a
// parsed date and formats back manually — Date.toISOString() would be correct
// here only because the parse is UTC-anchored, and relying on that is how the
// local/UTC confusion this codebase already fixed once creeps back in.
export function addDays(iso: string, n: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  // noUncheckedIndexedAccess types each destructured element as
  // `number | undefined`; a non-null assertion would silence that but the
  // repo lint bans `!`. A malformed date is a genuine bug upstream, so make
  // it loud rather than feeding NaN into Date.UTC and returning "NaN-NaN-NaN".
  if (y === undefined || m === undefined || d === undefined) {
    throw new Error(`addDays: malformed date "${iso}"`);
  }
  const t = new Date(Date.UTC(y, m - 1, d + n));
  const yy = t.getUTCFullYear();
  const mm = String(t.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(t.getUTCDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

// Month arithmetic on ISO date strings, so callers outside the lazy calendar
// chunk can do it without importing date-fns (DateRangePicker.tsx:24 —
// pulling that library into the eager bundle is what OH-64's whole-branch
// review flagged as CRITICAL 1). Only exercised here with a positive `n`
// (CALENDAR_MONTHS): `total`'s `% 12` relies on `total` staying non-negative,
// which holds for any realistic ISO date pushed forward, but is not a
// general-purpose "subtract months past year 0" guarantee.
export function addMonthsIso(iso: string, n: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (y === undefined || m === undefined || d === undefined) {
    throw new Error(`addMonthsIso: malformed date "${iso}"`);
  }
  const total = y * 12 + (m - 1) + n;
  const year = Math.floor(total / 12);
  const month = total % 12;
  const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const day = Math.min(d, lastDay);
  return `${String(year).padStart(4, '0')}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export function toCalendarMap(days: ApiCalendarDay[]): CalendarMap {
  return new Map(days.map((d) => [d.date, d]));
}

// parseIsoLocal turns a YYYY-MM-DD string into a LOCAL-midnight Date by
// constructing from y/m/d components — new Date(iso) parses as UTC and would
// misread the calendar fields on read-back. Mirrors the parseIso helper
// already used in DateRangeCalendar.tsx and BookingWidget.tsx (kept as a
// separate copy here rather than shared: this module has no React import and
// deliberately stays that way).
function parseIsoLocal(iso: string): Date {
  const [y, mo, d] = iso.split('-').map(Number);
  // noUncheckedIndexedAccess types each destructured element as
  // `number | undefined`; the repo lint bans `!`. A malformed date is a
  // genuine bug upstream, so fail loudly rather than feeding NaN into Date
  // (same idiom as addDays above).
  if (y === undefined || mo === undefined || d === undefined) {
    throw new Error(`parseIsoLocal: malformed date "${iso}"`);
  }
  return new Date(y, mo - 1, d);
}

// Locale fixed to en-GB, never `undefined`: the site is English-only and an
// implicit locale would make both the label and its tests depend on the
// runtime environment (OH-64 — this plan has already had two tests break on
// an unpinned real date; an unpinned locale is the same trap).
const DAY_MONTH = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' });
const DAY_MONTH_YEAR = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

function formatSide(date: Date, includeMonth: boolean, includeYear: boolean): string {
  if (!includeMonth) return String(date.getDate());
  return (includeYear ? DAY_MONTH_YEAR : DAY_MONTH).format(date);
}

// formatDateRangeLabel renders the compact, human-readable trigger label for
// the guest date picker (OH-64 — the previous label was the raw
// `${checkin} – ${checkout}` ISO pair, which is both machine-shaped and too
// wide for the hero search bar's flex-1 field).
//
// Rules:
//  - same calendar month + year: drop the repeated month off the start,
//    e.g. "5 – 6 Aug".
//  - different months: spell out both, e.g. "28 Aug – 3 Sep".
//  - year is shown only when the range is NOT entirely within `today`'s
//    year — appending it unconditionally eats space for no benefit on the
//    overwhelmingly common case of a booking made in the current year.
//  - when checkin and checkout land in DIFFERENT years (a range straddling
//    New Year), the year is shown on BOTH ends rather than only the last —
//    that ambiguity isn't resolved by the brief; this is the deliberate
//    call, e.g. "30 Dec 2026 – 2 Jan 2027". A same-year-but-not-current-year
//    range (e.g. a booking made entirely next January) still shows the year
//    only once, e.g. "5 – 8 Jan 2027".
//
// `today` is a required parameter, not `new Date()`, so this stays a pure
// function: the caller (DateRangePicker) already threads a `today` string
// through for the same reason.
export function formatDateRangeLabel(checkin: string, checkout: string, today: string): string {
  const start = parseIsoLocal(checkin);
  const end = parseIsoLocal(checkout);
  const currentYear = parseIsoLocal(today).getFullYear();

  const startYear = start.getFullYear();
  const endYear = end.getFullYear();
  const straddlesYear = startYear !== endYear;
  const showYear = straddlesYear || startYear !== currentYear || endYear !== currentYear;
  const sameCalendarMonth = !straddlesYear && start.getMonth() === end.getMonth();

  const startLabel = formatSide(start, !sameCalendarMonth, showYear && straddlesYear);
  const endLabel = formatSide(end, true, showYear);
  return `${startLabel} – ${endLabel}`;
}

// CANCELLATION_DEADLINE_FORMAT: fixed locale (en-GB, same rationale as
// DAY_MONTH/DAY_MONTH_YEAR above — the site is English-only and an implicit
// locale would make the string depend on the runtime environment), and a
// FIXED calendar/time shape rather than the compact day-only variants those
// two use: a cancellation deadline is read once, days or weeks after the
// booking was made, so it must always be unambiguous (day + short month +
// year + 24h clock), never the "12" vs "12 Aug" width-optimised shortening
// formatDateRangeLabel does for the picker's trigger label.
const CANCELLATION_DEADLINE_FORMAT: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
};

// formatCancellationDeadline renders the OH-68 §3.5 "free cancellation
// until" deadline for a guest — "12 Aug 2026, 14:00" — in the APARTMENT'S
// timezone, never the browser's (docs/27 §U14: a guest booking from another
// country must see the deadline that actually applies at the property).
// `atIso` is the raw UTC instant the server resolved (CancellationDeadline.at);
// `timezone` is its IANA zone (CancellationDeadline.timezone) — the two
// travel together on the wire for exactly this reason. Follows
// formatDateRangeLabel's idiom (a fixed-locale Intl.DateTimeFormat, no date
// library) rather than reaching for a new dependency.
//
// Returns null — never throws — on a bad instant or an unrecognised IANA
// zone name (Intl.DateTimeFormat throws a RangeError on either). A guest
// must see NO deadline line over a malformed API response, not a crashed
// page; callers omit the whole line when this returns null, matching this
// module's "unknown must never render as a specific claim" discipline
// (see from_price_minor's three-state doc in api/booking-openapi.yaml).
export function formatCancellationDeadline(atIso: string, timezone: string): string | null {
  try {
    const fmt = new Intl.DateTimeFormat('en-GB', { ...CANCELLATION_DEADLINE_FORMAT, timeZone: timezone });
    return fmt.format(new Date(atIso));
  } catch {
    return null;
  }
}

// isCancellationDeadlineInFuture reports whether the deadline instant has
// NOT yet passed relative to `now` (defaults to the real clock; tests pass
// a fixed one for determinism). OH-68 review round 1, IMPORTANT 1: a
// deadline that has already passed describes a window that is no longer
// open, whether because the booking's whole stay is long over (nothing in
// this codebase ever transitions a booking OUT of 'confirmed' — no write
// path sets checked_in/checked_out, so status alone cannot tell "hasn't
// happened yet" from "ended years ago") or, for a brand-new quote/hold on a
// near-term stay, because the 100%-tier cutoff already fell before today.
// Both are the same "false statement" this task exists to avoid. A
// malformed/unparseable instant is treated as NOT in the future (fails
// closed — never renders a claim it can't verify).
export function isCancellationDeadlineInFuture(atIso: string, now: Date = new Date()): boolean {
  const at = new Date(atIso).getTime();
  return !Number.isNaN(at) && at > now.getTime();
}

// resolveExistingBookingCancellationLabel composes the two independent
// gates an EXISTING booking (booking-view, cabinet) needs before showing a
// cancellation-deadline line, plus the formatting, so both call sites share
// exactly one robustness level (OH-68 review round 1, Minor 3:
// booking.$token.tsx lowercased status before comparing, cabinet.tsx
// didn't — this is now the one place that decision lives):
//   - status is 'confirmed' (case-insensitive, matching this codebase's own
//     StatusChip/BookingStatus mapping) — a cancelled booking's terms are
//     no longer actionable information.
//   - the deadline itself is still in the future (isCancellationDeadlineInFuture
//     above) — the IMPORTANT 1 fix: status alone can't tell a completed
//     stay from an upcoming one.
// Returns null (render nothing) whenever either gate fails, or there's no
// deadline to show at all (empty/malformed snapshot — see
// booking.DecodeCancellationPolicy on the server).
export function resolveExistingBookingCancellationLabel(
  status: string,
  deadline: { at: string; timezone: string } | null | undefined,
  now: Date = new Date(),
): string | null {
  if (status.toLowerCase() !== 'confirmed' || !deadline) return null;
  if (!isCancellationDeadlineInFuture(deadline.at, now)) return null;
  return formatCancellationDeadline(deadline.at, deadline.timezone);
}

// CALENDAR_MONTHS: one request covers the whole pricing horizon, so month
// navigation never refetches and adjacent windows never need stitching. The
// documented cost (OH-120) is that the very last date of the window cannot be
// picked as a checkout — a year out, where no rates are published anyway.
export const CALENDAR_MONTHS = 12;

export function apartmentCalendarQuery(id: string, from: string) {
  return queryOptions({
    queryKey: ['apartment-calendar', id, from],
    queryFn: async (): Promise<CalendarMap> => {
      const { data, error, response } = await getApartmentCalendar({
        path: { id },
        query: { from, months: CALENDAR_MONTHS },
      });
      if (error || !data) {
        throw new HttpError(response?.status ?? 0, 'failed to load calendar');
      }
      return toCalendarMap(data.days);
    },
    // The endpoint is Cache-Control: no-store and subtracts live holds, so a
    // long stale window would advertise nights someone is already paying for.
    staleTime: 60_000,
    retry: (failureCount, err) => {
      if (err instanceof HttpError && err.status === 404) return false;
      return failureCount < 2;
    },
  });
}
